import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Sparkles, 
  FileText, 
  Image, 
  Mail, 
  MessageSquare, 
  Layout, 
  Phone, 
  Users, 
  Target,
  TrendingUp,
  CheckCircle,
  Wrench,
  Droplets,
  Zap,
  ArrowRight,
  Search,
  Filter,
  Calendar,
  BarChart3,
  Download,
  Rocket,
  Shield,
  Zap as Lightning
} from 'lucide-react';

const Onboarding = () => {
  const assetTypes = [
    { icon: FileText, label: 'Campaign Playbooks', color: 'text-construction-orange' },
    { icon: Image, label: 'Facebook & Google Ads', color: 'text-blue-400' },
    { icon: Mail, label: 'Email Campaigns', color: 'text-green-400' },
    { icon: MessageSquare, label: 'SMS Campaigns', color: 'text-purple-400' },
    { icon: Layout, label: 'Landing Pages', color: 'text-cyan-400' },
    { icon: Phone, label: 'Sales Scripts', color: 'text-yellow-400' },
    { icon: Users, label: 'CSR Scripts', color: 'text-orange' },
    { icon: Image, label: 'Canva Templates', color: 'text-orange' },
    { icon: Sparkles, label: 'AI Prompts', color: 'text-construction-orange' },
    { icon: BarChart3, label: 'Tracking Sheets', color: 'text-indigo-400' },
    { icon: CheckCircle, label: 'Deployment Checklists', color: 'text-green-400' },
    { icon: Calendar, label: 'Seasonal Campaigns', color: 'text-blue-400' },
  ];

  const trades = [
    { icon: Wrench, label: 'HVAC', gradient: 'from-[#B4FF39] to-[#8FD42F]', link: '/campaigns/hvac' },
    { icon: Droplets, label: 'Plumbing', gradient: 'from-blue-500 to-cyan-500', link: '/campaigns/plumbing' },
    { icon: Zap, label: 'Electrical', gradient: 'from-[#E8731A] to-[#FF8C42]', link: '/campaigns/electrical' },
  ];

  const benefits = [
    { icon: Shield, title: 'No More Guessing', description: 'Every campaign is proven, tested, and ready to deploy' },
    { icon: TrendingUp, title: 'Consistent Results', description: 'Stop the feast-or-famine cycle with systematic marketing' },
    { icon: Lightning, title: 'Launch in Minutes', description: 'Customize and deploy campaigns in under an hour' },
  ];

  return (
    <div className="min-h-screen bg-deep-navy">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-blue via-deep-navy to-dark-steel opacity-50"></div>
        <div className="relative max-w-6xl mx-auto px-6 py-20 text-center">
          <div className="inline-flex items-center gap-2 bg-construction-orange/20 border border-construction-orange px-4 py-2 rounded-full mb-6">
            <Wrench className="text-construction-orange" size={20} />
            <span className="text-construction-orange font-medium">Welcome to GritClicks</span>
          </div>
          
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
            Your Complete Marketing Department—<br />Available 24/7
          </h1>
          
          <p className="text-xl text-light-gray max-w-4xl mx-auto mb-10 leading-relaxed">
            GritClicks is a searchable library of proven marketing campaigns, sales systems, 
            scripts, templates, and AI tools built exclusively for HVAC, Plumbing, and Electrical contractors. 
            Instead of wondering what to market next, simply choose a campaign, customize it, and launch.
          </p>
          
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/campaigns" className="btn-primary text-lg px-8 py-4 inline-flex items-center gap-2">
              <Target size={24} />
              Browse Campaigns
            </Link>
            <button className="btn-secondary text-lg px-8 py-4 inline-flex items-center gap-2">
              <FileText size={24} />
              View Getting Started Guide
            </button>
          </div>
        </div>
      </section>

      {/* What Is GritClicks Section */}
      <section className="max-w-6xl mx-auto px-6 py-20">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-white mb-4">What Is GritClicks?</h2>
          <div className="w-24 h-1 bg-construction-orange mx-auto mb-6"></div>
        </div>

        <div className="card mb-8">
          <p className="text-xl text-light-gray leading-relaxed mb-6">
            This is <span className="text-white font-bold">NOT</span> another CRM, marketing agency, or software platform.
          </p>
          <p className="text-xl text-white font-bold mb-6">
            It is a complete marketing operating system that contains ready-to-use assets.
          </p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3 text-light-gray">
            {[
              'Campaign Playbooks', 'Facebook Posts', 'Instagram Posts', 'Google Business Profile Posts',
              'Google Ads', 'Facebook Ads', 'Landing Pages', 'Email Campaigns', 'SMS Campaigns',
              'CSR Scripts', 'Sales Scripts', 'Technician Talking Points', 'Financing Scripts',
              'Referral Campaigns', 'Review Campaigns', 'Canva Templates', 'AI Customization Prompts',
              'KPI Tracking Templates'
            ].map((item, index) => (
              <div key={index} className="flex items-center gap-2">
                <CheckCircle className="text-construction-orange flex-shrink-0" size={20} />
                <span>{item}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="card bg-construction-orange/10 border-2 border-construction-orange">
          <p className="text-xl text-white font-medium text-center">
            Everything has already been created. Members simply choose the campaign that fits their business and customize it.
          </p>
        </div>
      </section>

      {/* Why This Matters */}
      <section className="bg-slate-blue py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">Why This Matters</h2>
            <div className="w-24 h-1 bg-construction-orange mx-auto mb-6"></div>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div className="card bg-dark-steel">
              <h3 className="text-2xl font-bold text-white mb-6">Common Frustrations:</h3>
              <div className="space-y-3 text-light-gray">
                {[
                  'Running out of marketing ideas',
                  'Paying agencies thousands every month',
                  'Inconsistent lead generation',
                  'Slow seasons killing revenue',
                  'Wasted ad spend',
                  'Employees not following consistent messaging',
                  'Marketing that isn\'t documented'
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-orange mt-2 flex-shrink-0"></div>
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="card bg-dark-steel border-2 border-construction-orange">
              <h3 className="text-2xl font-bold text-white mb-6">The Vault Solution:</h3>
              <div className="space-y-4">
                {benefits.map((benefit, index) => {
                  const Icon = benefit.icon;
                  return (
                    <div key={index} className="flex items-start gap-4">
                      <div className="p-3 bg-construction-orange/20 rounded-lg">
                        <Icon className="text-construction-orange" size={24} />
                      </div>
                      <div>
                        <h4 className="text-lg font-bold text-white mb-1">{benefit.title}</h4>
                        <p className="text-light-gray">{benefit.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="card text-center bg-construction-orange/10 border-2 border-construction-orange">
            <p className="text-xl text-white font-medium">
              Organized, repeatable marketing systems that work—without the agency fees.
            </p>
          </div>
        </div>
      </section>

      {/* How To Use */}
      <section className="max-w-6xl mx-auto px-6 py-20">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-white mb-4">How To Use The Vault</h2>
          <div className="w-24 h-1 bg-construction-orange mx-auto mb-6"></div>
          <p className="text-xl text-light-gray">5 simple steps to launch your next campaign</p>
        </div>

        <div className="space-y-8">
          {/* Step 1 */}
          <div className="card bg-dark-steel">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 rounded-full bg-construction-orange flex items-center justify-center text-white font-bold text-xl">
                1
              </div>
              <h3 className="text-2xl font-bold text-white">Choose Your Trade</h3>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              {trades.map((trade, index) => {
                const Icon = trade.icon;
                return (
                  <Link
                    key={index}
                    to={trade.link}
                    className="card hover:scale-105 transition-all duration-200"
                  >
                    <div className={`h-2 rounded-t-lg bg-gradient-to-r ${trade.gradient} -mt-6 -mx-6 mb-4`}></div>
                    <div className="flex items-center gap-3">
                      <div className={`p-3 bg-gradient-to-br ${trade.gradient} rounded-xl text-white`}>
                        <Icon size={24} />
                      </div>
                      <span className="text-xl font-bold text-white">{trade.label}</span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>

          <div className="flex justify-center">
            <ArrowRight className="text-construction-orange" size={48} />
          </div>

          {/* Step 2 */}
          <div className="card bg-dark-steel">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 rounded-full bg-construction-orange flex items-center justify-center text-white font-bold text-xl">
                2
              </div>
              <h3 className="text-2xl font-bold text-white">Choose Your Goal</h3>
            </div>
            <div className="grid md:grid-cols-4 gap-3">
              {[
                'Generate Leads',
                'Book More Calls',
                'Sell Replacements',
                'Grow Memberships',
                'Increase Reviews',
                'Generate Referrals',
                'Commercial Marketing',
                'Hiring'
              ].map((goal, index) => (
                <div key={index} className="bg-slate-blue px-4 py-3 rounded-lg text-center">
                  <span className="text-light-gray font-medium">{goal}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-center">
            <ArrowRight className="text-construction-orange" size={48} />
          </div>

          {/* Step 3 */}
          <div className="card bg-dark-steel">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-construction-orange flex items-center justify-center text-white font-bold text-xl">
                3
              </div>
              <h3 className="text-2xl font-bold text-white">Open the Campaign</h3>
            </div>
          </div>

          <div className="flex justify-center">
            <ArrowRight className="text-construction-orange" size={48} />
          </div>

          {/* Step 4 */}
          <div className="card bg-dark-steel">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 rounded-full bg-construction-orange flex items-center justify-center text-white font-bold text-xl">
                4
              </div>
              <h3 className="text-2xl font-bold text-white">Customize Using AI</h3>
            </div>
            <div className="flex items-center gap-4 bg-slate-blue p-4 rounded-lg">
              <Wrench className="text-construction-orange" size={32} />
              <p className="text-light-gray">
                Use the included AI prompts to personalize the campaign with your brand, services, and local market
              </p>
            </div>
          </div>

          <div className="flex justify-center">
            <ArrowRight className="text-construction-orange" size={48} />
          </div>

          {/* Step 5 */}
          <div className="card bg-dark-steel">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 rounded-full bg-construction-orange flex items-center justify-center text-white font-bold text-xl">
                5
              </div>
              <h3 className="text-2xl font-bold text-white">Launch Through Your Channels</h3>
            </div>
            <p className="text-light-gray">
              Deploy through Facebook, Google, email, SMS, or any marketing platform you already use
            </p>
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="bg-slate-blue py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">What's Included</h2>
            <div className="w-24 h-1 bg-construction-orange mx-auto mb-6"></div>
          </div>

          <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-6">
            {assetTypes.map((asset, index) => {
              const Icon = asset.icon;
              return (
                <div key={index} className="card text-center hover:scale-105 transition-all duration-200">
                  <div className="w-16 h-16 mx-auto mb-4 bg-slate-blue rounded-xl flex items-center justify-center">
                    <Icon className={asset.color} size={32} />
                  </div>
                  <p className="text-white font-medium">{asset.label}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It's Organized */}
      <section className="max-w-6xl mx-auto px-6 py-20">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-white mb-4">How Everything Is Organized</h2>
          <div className="w-24 h-1 bg-construction-orange mx-auto mb-6"></div>
          <p className="text-xl text-light-gray">Find exactly what you need in seconds</p>
        </div>

        <div className="grid md:grid-cols-5 gap-6">
          {[
            { icon: Wrench, label: 'By Trade', description: 'HVAC, Plumbing, Electrical' },
            { icon: Calendar, label: 'By Season', description: 'Summer, Winter, Spring, Fall' },
            { icon: Target, label: 'By Goal', description: 'Leads, Sales, Reviews, Referrals' },
            { icon: Layout, label: 'By Channel', description: 'Ads, Email, Social, Direct Mail' },
            { icon: TrendingUp, label: 'By Objective', description: 'Revenue, Growth, Retention' },
          ].map((org, index) => {
            const Icon = org.icon;
            return (
              <div key={index} className="card text-center">
                <div className="w-12 h-12 mx-auto mb-3 bg-construction-orange/20 rounded-full flex items-center justify-center">
                  <Icon className="text-construction-orange" size={24} />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{org.label}</h3>
                <p className="text-sm text-light-gray">{org.description}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Who This Is For */}
      <section className="bg-dark-steel py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">Who This Is For</h2>
            <div className="w-24 h-1 bg-construction-orange mx-auto mb-6"></div>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-8">
            {[
              { icon: Wrench, label: 'HVAC Contractors', description: 'Heating, cooling, and air quality specialists' },
              { icon: Droplets, label: 'Plumbing Contractors', description: 'Residential and commercial plumbing services' },
              { icon: Zap, label: 'Electrical Contractors', description: 'Electrical installation and repair services' },
            ].map((audience, index) => {
              const Icon = audience.icon;
              return (
                <div key={index} className="card text-center">
                  <div className="w-20 h-20 mx-auto mb-4 bg-construction-orange/20 rounded-full flex items-center justify-center">
                    <Icon className="text-construction-orange" size={40} />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">{audience.label}</h3>
                  <p className="text-light-gray">{audience.description}</p>
                </div>
              );
            })}
          </div>

          <div className="card text-center bg-construction-orange/10 border-2 border-construction-orange">
            <p className="text-xl text-white">
              Businesses wanting to market consistently without hiring a large marketing agency.
            </p>
          </div>
        </div>
      </section>

      {/* Getting Best Results */}
      <section className="max-w-6xl mx-auto px-6 py-20">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-white mb-4">Getting The Best Results</h2>
          <div className="w-24 h-1 bg-construction-orange mx-auto mb-6"></div>
        </div>

        <div className="card">
          <div className="flex items-start gap-4 mb-6">
            <div className="p-4 bg-construction-orange rounded-xl">
              <Rocket className="text-white" size={32} />
            </div>
            <div>
              <h3 className="text-2xl font-bold text-white mb-2">Consistent Marketing Wins</h3>
              <p className="text-light-gray text-lg">
                Success comes from systematic execution, not one-off campaigns.
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <CheckCircle className="text-construction-orange flex-shrink-0 mt-1" size={20} />
              <div>
                <p className="text-white font-medium mb-1">Implement one campaign every month</p>
                <p className="text-light-gray text-sm">Build momentum with regular deployment</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="text-construction-orange flex-shrink-0 mt-1" size={20} />
              <div>
                <p className="text-white font-medium mb-1">Follow the deployment checklist</p>
                <p className="text-light-gray text-sm">Execute properly with step-by-step guidance</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="text-construction-orange flex-shrink-0 mt-1" size={20} />
              <div>
                <p className="text-white font-medium mb-1">Measure results with the KPI template</p>
                <p className="text-light-gray text-sm">Track what works and optimize over time</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="text-construction-orange flex-shrink-0 mt-1" size={20} />
              <div>
                <p className="text-white font-medium mb-1">Build momentum over time</p>
                <p className="text-light-gray text-sm">Compound growth through consistent marketing</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Ongoing Improvements */}
      <section className="bg-slate-blue py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">The Vault Continues To Improve</h2>
            <div className="w-24 h-1 bg-construction-orange mx-auto mb-6"></div>
            <p className="text-xl text-light-gray">
              A living marketing library that evolves with the home service industry
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              'New campaign templates',
              'Better AI prompts',
              'Updated marketing strategies',
              'Additional seasonal campaigns',
              'New design templates',
              'Industry-specific resources',
              'Feature enhancements',
              'Improved organization and search',
              'Customer-requested additions'
            ].map((improvement, index) => (
              <div key={index} className="card flex items-center gap-3">
                <div className="w-10 h-10 bg-construction-orange/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="text-construction-orange" size={20} />
                </div>
                <p className="text-white font-medium">{improvement}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="max-w-6xl mx-auto px-6 py-20">
        <div className="card text-center bg-gradient-to-br from-construction-orange/20 to-construction-orange/5 border-2 border-construction-orange">
          <h2 className="text-4xl font-bold text-white mb-4">
            Ready to Launch Your Next Campaign?
          </h2>
          <p className="text-xl text-light-gray mb-8 max-w-3xl mx-auto">
            Stop guessing what to market next. Find a campaign, customize it in minutes, 
            and start generating more calls, more bookings, and more revenue.
          </p>
          <Link to="/campaigns" className="btn-primary text-lg px-10 py-4 inline-flex items-center gap-2">
            <Rocket size={24} />
            Browse Campaign Library
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Onboarding;
